// EWM-315 — Pack Size Display.
//
// Derives the human-readable pack-size label rendered in the PDP info panel
// from the CommerceTools `freightAttributes` variant attribute. That attribute
// reaches the FE as a JSON-encoded STRING (confirmed live on QM1500 / WH3098,
// en-AU + en-NZ), so the mapper parses it with `parseJsonAttr` before calling
// this helper. Accepting an already-parsed object too keeps the function pure
// and tolerant of a future BFF revision that deep-parses the attribute.
//
// Formula (from the ticket):
//   uomWholesaleConv > 1  → "{uomWholesaleFriendlyName} ({uomWholesaleConv} {uomRetailFriendlyName})"
//                           e.g. "Roll (20 Metre)"
//   uomWholesaleConv === 1 → "{uomWholesaleConv} {uomWholesaleFriendlyName}"
//                           e.g. "1 Each"
// Anything incomplete → undefined (the row hides, matching brand / MPN behaviour).

interface FreightAttributes {
  uomWholesaleConv?: unknown;
  uomWholesaleFriendlyName?: unknown;
  uomRetailFriendlyName?: unknown;
}

const asNonEmptyString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined;

export const derivePackSize = (freight: unknown): string | undefined => {
  let parsed: unknown = freight;

  if (typeof freight === 'string') {
    if (!freight.trim()) return undefined;
    try {
      parsed = JSON.parse(freight);
    } catch {
      return undefined;
    }
  }

  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return undefined;

  const { uomWholesaleConv, uomWholesaleFriendlyName, uomRetailFriendlyName } = parsed as FreightAttributes;

  if (typeof uomWholesaleConv !== 'number' || !Number.isFinite(uomWholesaleConv) || uomWholesaleConv < 1) {
    return undefined;
  }

  const wholesaleName = asNonEmptyString(uomWholesaleFriendlyName);
  if (!wholesaleName) return undefined;

  if (uomWholesaleConv > 1) {
    const retailName = asNonEmptyString(uomRetailFriendlyName);
    if (!retailName) return undefined;
    return `${wholesaleName} (${uomWholesaleConv} ${retailName})`;
  }

  // uomWholesaleConv === 1
  return `${uomWholesaleConv} ${wholesaleName}`;
};
